import { Component, EventEmitter, Inject, Input, OnDestroy, Optional, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ScrollFreezeServiceTypes } from '@frontend/sports/common/core/feature/scroll-freeze';
import { ElementProvider } from '@frontend/sports/common/core/utils/element-provider';
import { MODAL_DIALOG_CONTAINER, ModalDialogService, ModalRef } from '@frontend/sports/modal/feature';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { IntlService, SessionStoreService } from '@frontend/vanilla/core';

import { MyBetsListContext } from '../../../my-bets-base/models/my-bets-viewmodels';
import {
    IFilterConstraint,
    MyBetsDateRangeFilterContstraint,
    MyBetsFilterConstraint,
    MyBetsStateFilterContstraint,
} from '../../models/my-bets-filter-constraints';
import { IMyBetsListRequest } from '../../services/my-bets-api.service';
import { MyBetsPrintService } from '../../services/my-bets-print.service';
import { MyBetsListService } from '../betslip-list/my-bets-list.service';
import { IMyBetsSettledFiltersProps, MyBetsSettledFiltersComponent } from './my-bets-settled-filters.component';

@Component({
    selector: 'ms-my-bets-filter',
    templateUrl: './my-bets-filter.html',
})
export class MyBetsFilterComponent implements OnDestroy {
    @Input() filterConstraint: MyBetsFilterConstraint;
    @Input() inColumn = false;
    @Input() myBetsListContext: MyBetsListContext;
    @Input() betslipsCount: number;

    @Output() filterChanged: EventEmitter<MyBetsFilterConstraint> = new EventEmitter<MyBetsFilterConstraint>();

    private openModalHandle: ModalRef | null;
    props: IMyBetsSettledFiltersProps = { stateModel: '' };

    toggleResponsiveFilter = false;
    isRangeSet = false;
    isResponsiveMode = false;

    private stateFilterConstraintSessionStorageKey = 'stateConstraint';
    private dateRangeFilterConstraintSessionStorageKey = 'dateRangeConstraint';

    constructor(
        private modalDialogService: ModalDialogService,
        private trackingService: TrackingService,
        @Optional()
        @Inject(MODAL_DIALOG_CONTAINER)
        private modalDialogContainerProvider: ElementProvider,
        private sitecore: Sitecore,
        private myBetsService: MyBetsListService,
        private sessionStorage: SessionStoreService,
        private printService: MyBetsPrintService,
        private intlService: IntlService,
    ) {
        this.myBetsService
            .asObservable()
            .pipe(takeUntilDestroyed())
            .subscribe((columnCount) => {
                this.isResponsiveMode = columnCount > 1;
                if (this.isResponsiveMode && !this.inColumn) {
                    this.closeModalHandle();
                }
            });
    }

    print(): void {
        const dateConstraint = this.filterConstraint.getConstraint(MyBetsDateRangeFilterContstraint);
        const stateConstraint = this.filterConstraint.getConstraint(MyBetsStateFilterContstraint);

        this.printService.printMyBets(
            dateConstraint?.fromDate,
            dateConstraint?.toDate,
            stateConstraint?.state,
            this.myBetsListContext.associatedAccount,
        );
    }

    async showFilter(): Promise<void> {
        if (this.isResponsiveMode && !this.inColumn) {
            this.toggleResponsiveFilter = !this.toggleResponsiveFilter;
            if (this.filterConstraint && this.filterConstraint.constraints.length > 0) {
                this.fillConstraintProps();
            }

            return;
        }

        this.closeModalHandle();
        this.fillConstraintProps();

        this.openModalHandle = this.modalDialogService.openDialog(MyBetsSettledFiltersComponent, {
            data: this.props,
            settings: {
                isModalDialog: false,
                cssClass: 'my-bets-filters-modal-popup',
                fit: this.inColumn,
                showDialogHeader: true,
                title: this.sitecore.myBets_FilterView.Title,
                container: this.modalDialogContainerProvider?.element?.nativeElement,
                inColumn: this.inColumn,
                scrollFreezeType: this.inColumn ? ScrollFreezeServiceTypes.None : ScrollFreezeServiceTypes.All,
            },
        });

        this.openModalHandle.result
            .then((result) => {
                if (result) {
                    this.handleConstraints(result.constaints);
                }
            })
            .catch(() => {})
            .finally(() => (this.openModalHandle = null));

        this.trackingService.update({ [trackingConstants.PAGE_REFERRING_ACTION]: 'mybets_Filter' });
    }

    onConstraintRemoved(constraint: IFilterConstraint<IMyBetsListRequest>): void {
        this.filterConstraint.removeConstraint(constraint);
        this.filterChanged.emit(this.filterConstraint);
    }

    onFilter(constraints: IFilterConstraint<IMyBetsListRequest>[]): void {
        if (this.isResponsiveMode) {
            this.handleConstraints(constraints);
        }
    }

    ngOnDestroy(): void {
        this.closeModalHandle();
    }

    private closeModalHandle(): void {
        if (this.openModalHandle) {
            this.openModalHandle.close();
            this.openModalHandle = null;
        }
    }

    private handleConstraints(constraints: IFilterConstraint<IMyBetsListRequest>[]): void {
        this.filterConstraint.clean();
        for (const c of constraints) {
            this.filterConstraint.addConstraint(c);
        }
        this.filterChanged.emit(this.filterConstraint);

        if (!this.inColumn) {
            this.sessionStorage.set(this.stateFilterConstraintSessionStorageKey, this.filterConstraint.getConstraint(MyBetsStateFilterContstraint));
            this.sessionStorage.set(
                this.dateRangeFilterConstraintSessionStorageKey,
                this.filterConstraint.getConstraint(MyBetsDateRangeFilterContstraint),
            );
        }
    }

    private fillConstraintProps(): void {
        const state = this.filterConstraint.getConstraint(MyBetsStateFilterContstraint);
        const range = this.filterConstraint.getConstraint(MyBetsDateRangeFilterContstraint);

        this.props = { stateModel: '' };

        if (state) {
            this.props.stateModel = state.state;
        }
        if (range) {
            this.isRangeSet = true;

            if (range.fromDate) {
                this.props.fromDate = this.intlService.formatDate(range.fromDate, 'yyyy-MM-dd');
            }
            if (range.toDate) {
                this.props.toDate = this.intlService.formatDate(range.toDate, 'yyyy-MM-dd');
            }
        }
    }
}
